import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scroll, BookMarked, Map, Flame, Shield, LogOut, Crown, Compass, Feather, KeyRound, Snowflake, Mail, Award, Coins, Sword, MessageCircle } from 'lucide-react';
import { codexApi } from '../api';

const NAV = [
  { to: '/codex/dashboard', label: 'Dashboard', icon: Compass },
  { to: '/codex/scroll', label: "Today's Scroll", icon: Scroll },
  { to: '/codex/missions', label: 'Missions', icon: Sword },
  { to: '/codex/codex-book', label: 'My Codex', icon: BookMarked },
  { to: '/codex/chambers', label: 'Vault', icon: KeyRound },
  { to: '/codex/journal', label: 'Journal', icon: Feather },
  { to: '/codex/letters', label: 'Letters', icon: Mail },
  { to: '/codex/chat', label: 'Chat', icon: MessageCircle },
  { to: '/codex/achievements', label: 'Hall of Seals', icon: Award },
  { to: '/codex/treasury', label: 'Treasury', icon: Coins },
  { to: '/codex/journey', label: 'Journey', icon: Map },
];

export default function CodexNav({ user, isMentor, progress }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const [unreadLetters, setUnreadLetters] = useState(0);

  useEffect(() => {
    codexApi.inbox()
      .then((items) => setUnreadLetters((items || []).filter((i) => !i.read).length))
      .catch(() => {});
  }, [loc.pathname]);

  return (
    <header
      className="codex-card"
      style={{
        margin: '14px',
        marginBottom: 0,
        padding: '12px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        flexWrap: 'wrap',
      }}
      data-testid="codex-nav"
    >
      <Link to="/codex/dashboard" className="codex-display" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--codex-gold-light)', textDecoration: 'none' }}>
        <motion.span
          animate={{ rotate: [0, 4, -4, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ display: 'inline-flex' }}
        >
          <Scroll size={22} color="var(--codex-gold)" />
        </motion.span>
        <span style={{ fontWeight: 700, letterSpacing: '0.2em', fontSize: 14 }}>CODEX</span>
        <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: '0.3em' }}>THE ANCIENT SCROLLS</span>
      </Link>

      <nav style={{ display: 'flex', gap: 4, marginLeft: 12, flexWrap: 'wrap' }}>
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = loc.pathname === item.to;
          const isLetters = item.to === '/codex/letters';
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`codex-nav-${item.label.replace(/[^a-z]/gi, '').toLowerCase()}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 10,
                fontSize: 13,
                color: active ? 'var(--codex-gold-light)' : 'rgba(253,246,227,0.7)',
                background: active ? 'rgba(212,175,55,0.15)' : 'transparent',
                border: '1px solid ' + (active ? 'rgba(212,175,55,0.4)' : 'transparent'),
                textDecoration: 'none',
                transition: 'all 0.18s ease',
                position: 'relative',
              }}
            >
              <Icon size={15} />
              {item.label}
              {isLetters && unreadLetters > 0 && (
                <span
                  data-testid="codex-letters-unread-badge"
                  style={{
                    background: 'var(--codex-orange)',
                    color: '#1a0e2c',
                    fontSize: 10,
                    fontWeight: 800,
                    minWidth: 18,
                    height: 18,
                    padding: '0 5px',
                    borderRadius: 9,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {unreadLetters}
                </span>
              )}
            </Link>
          );
        })}
        {isMentor && (
          <Link
            to="/codex/mentor"
            data-testid="codex-nav-mentor"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 10,
              fontSize: 13,
              color: 'var(--codex-gold-light)',
              background: 'linear-gradient(135deg, rgba(212,175,55,0.18), rgba(168,85,247,0.18))',
              border: '1px solid rgba(212,175,55,0.4)',
              textDecoration: 'none',
            }}
          >
            <Crown size={15} />
            Mentor
          </Link>
        )}
        {isMentor && (
          <Link
            to="/codex/content"
            data-testid="codex-nav-content"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 10,
              fontSize: 13,
              color: 'var(--codex-gold-light)',
              background: 'rgba(168,85,247,0.12)',
              border: '1px solid rgba(168,85,247,0.35)',
              textDecoration: 'none',
            }}
          >
            <Compass size={15} />
            Content
          </Link>
        )}
      </nav>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {progress && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }} data-testid="codex-streak">
              <Flame size={16} color="var(--codex-orange)" />
              <span>{progress.streak_days || 0}d</span>
            </div>
            {progress.streak_freezes > 0 && (
              <div
                title={`${progress.streak_freezes} streak shield${progress.streak_freezes === 1 ? '' : 's'} — auto-protects you if you miss a day`}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#7dd3fc' }}
                data-testid="codex-freezes"
              >
                <Snowflake size={15} />
                <span>{progress.streak_freezes}</span>
              </div>
            )}
            <Link
              to="/codex/treasury"
              title={`${progress.shekels || 0} Shekels`}
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--codex-gold-light)', textDecoration: 'none' }}
              data-testid="codex-shekels-chip"
            >
              <Coins size={15} color="var(--codex-gold)" />
              <span>{progress.shekels || 0}</span>
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }} data-testid="codex-level">
              <Shield size={16} color="var(--codex-gold)" />
              <span>Lv {progress.level} · {progress.title}</span>
            </div>
          </>
        )}
        <button
          onClick={() => navigate('/dashboard')}
          className="codex-btn codex-btn-ghost"
          style={{ padding: '8px 14px', fontSize: 12 }}
          data-testid="codex-back-to-academy"
          title="Back to Academy"
        >
          <LogOut size={14} /> Academy
        </button>
      </div>
    </header>
  );
}
